import React, { useState, useEffect } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Image,
  Row,
  Tab,
  Tabs,
} from "react-bootstrap";
import {
  primaryColor,
  secondaryColor,
  textDarkColor,
} from "../../../Constants/Colors";
import { GrLinkNext, GrLinkPrevious } from "react-icons/gr";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import { FaRegCalendarAlt } from "react-icons/fa";
import { GiBrain } from "react-icons/gi";
import { Link } from "react-router-dom";
import { LuArrowUpRight } from "react-icons/lu";
import { useGetBlogsQuery } from "../../../redux/api/BlogsApi/blogsApi";
import { useGetBlogCategoriesQuery } from "../../../redux/api/BlogCategoriesApi/blogCategoriesApi";
import { useGetSettingsQuery } from "../../../redux/api/SettingsApi/settingsApi";
import LoaderSpinner from "../../../Components/LoaderSpinner";
import SEO from "../../../Components/SEO";
import "./Blogs.css";

const Blogs = () => {
  const [activeTab, setActiveTab] = useState("all");
  const { data: settings } = useGetSettingsQuery();

  // Scroll to top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch Categories
  const { data: categoriesData, isLoading: isCatsLoading } =
    useGetBlogCategoriesQuery();
  const categories = categoriesData?.filter((c) => c.status) || [];

  // Fetch Blogs (latest first)
  const { data: blogsData, isLoading: isBlogsLoading } = useGetBlogsQuery({
    status: true,
    sortBy: "published_at",
    order: "DESC",
    limit: 100,
    categoryId: activeTab === "all" ? "" : activeTab,
  });
  const blogPosts = blogsData?.blogs || [];

  // Fetch Top Rated Blogs (ordered by view count)
  const { data: topReadsData } = useGetBlogsQuery({
    status: true,
    sortBy: "view_count",
    order: "DESC",
    limit: 5,
  });
  const topReads = topReadsData?.blogs || [];

  if (isCatsLoading || isBlogsLoading) {
    return (
      <div className="py-5 text-center">
        <LoaderSpinner />
      </div>
    );
  }

  const BlogCard = ({ post }) => (
    <Card
      style={{
        color: textDarkColor,
        borderRadius: "18px",
        border: "none",
      }}
      className="overflow-hidden blog-card shadow-sm h-100"
    >
      <CardBody className="bg-light align-items-center d-flex flex-column h-100">
        <Image
          src={post.image || "https://via.placeholder.com/400x250"}
          className="img-fluid rounded-3 mb-3 w-100"
          style={{ height: "200px", objectFit: "cover" }}
        />
        <div className="d-flex gap-3 mb-2 w-100 justify-content-start">
          <div className="d-flex align-items-center gap-2">
            <FaRegCalendarAlt color={primaryColor} />
            <time style={{ fontSize: "12px" }} className="m-0">
              {new Date(post.published_at).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </time>
          </div>
          <Badge className="d-flex align-items-center gap-2" bg="info">
            <GiBrain color="#F07371" className="fw-bold" />
            <span style={{ fontSize: "12px" }}>
              {post.category?.name || "General"}
            </span>
          </Badge>
        </div>
        <div className="text-start w-100 mb-3">
          <h6 className="fw-bold">{post.title}</h6>
          <p
            className="text-muted"
            style={{
              fontSize: "14px",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {post.summary}
          </p>
        </div>
        <div className="mt-auto w-100 text-start">
          <Link
            to={`/blogs/${post.slug}`}
            style={{ color: primaryColor }}
            className="arrowLauncher-container text-decoration-none fw-semibold"
          >
            Read More <LuArrowUpRight className="fs-5 arrowLauncher" />
          </Link>
        </div>
      </CardBody>
    </Card>
  );

  return (
    <Container className="">
      <SEO
        title="Blog & Insights | Ideal Corporate Services"
        description={settings?.meta_description}
        keywords={settings?.meta_keyword}
      />
      <Row className="mb-5">
        <Col
          xs={12}
          md={10}
          lg={6}
          className="m-auto d-flex flex-column justify-content-center"
        >
          <div
            style={{
              textAlign: "center",
            }}
          >
            <Button
              style={{
                backgroundColor: "#009dff",
                border: "none",
                boxShadow: "0 4px 20px rgba(0, 157, 255, 0.3)",
              }}
              className="rounded-pill my-2 px-4"
            >
              Blogs
            </Button>

            <h1
              className="my-3"
              style={{ color: primaryColor, fontWeight: "bold" }}
            >
              From Ideas to Impact
            </h1>

            <p className="text-muted mb-4">
              Explore expert-written insights, growth strategies, and proven
              tools to elevate your brand and business performance.
            </p>
          </div>
        </Col>
      </Row>

      {topReads.length > 0 && (
        <Row className="mb-5">
          <Col>
            <h5 style={{ color: primaryColor, fontWeight: "bold" }}>
              Top Rated Blogs
            </h5>
            <p>
              What everyone in digital, tech, and design is reading this week.
            </p>
            <div className="px-0 px-md-3">
              <Swiper
                modules={[Autoplay, Navigation, Pagination]}
                autoplay={{ delay: 3000, disableOnInteraction: false }}
                loop={topReads.length > 1}
                slidesPerView={"auto"}
                spaceBetween={20}
                pagination={{ clickable: true, el: ".custom-pagination" }}
                navigation={{ nextEl: ".custom-next", prevEl: ".custom-prev" }}
                breakpoints={{
                  0: { slidesPerView: 1 },
                  992: { slidesPerView: topReads.length > 1 ? 2 : 1 },
                }}
                className="mySwiper py-1"
              >
                {topReads.map((post) => (
                  <SwiperSlide key={post.id} style={{ maxWidth: "700px" }}>
                    <div
                      style={{
                        padding: "2px",
                        borderRadius: "20px",
                        background: `linear-gradient(to right, ${secondaryColor}, ${primaryColor})`,
                      }}
                    >
                      <Card
                        style={{
                          color: textDarkColor,
                          cursor: "pointer",
                          borderRadius: "18px",
                          border: "none",
                        }}
                        className="overflow-hidden"
                      >
                        <CardBody>
                          <Row className="align-items-center d-flex">
                            <Col xs={12} md={6}>
                              <Image
                                src={
                                  post.image ||
                                  "https://via.placeholder.com/400x300"
                                }
                                className="img-fluid rounded-3 w-100"
                                style={{ height: "180px", objectFit: "cover" }}
                              />
                            </Col>
                            <Col xs={12} md={6} className="mt-3 mt-md-0">
                              <div className="d-flex gap-3 mb-2">
                                <div className="d-flex align-items-center gap-2">
                                  <FaRegCalendarAlt color={primaryColor} />
                                  <time
                                    style={{ fontSize: "12px" }}
                                    className="m-0"
                                  >
                                    {new Date(
                                      post.published_at,
                                    ).toLocaleDateString("en-GB", {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                    })}
                                  </time>
                                </div>
                                <Badge
                                  className="d-flex align-items-center gap-2"
                                  bg="info"
                                >
                                  <GiBrain
                                    color="#F07371"
                                    className="fw-bold"
                                  />
                                  <span style={{ fontSize: "12px" }}>
                                    {post.category?.name || "General"}
                                  </span>
                                </Badge>
                              </div>

                              <div>
                                <h6 className="fw-bold text-truncate-2">
                                  {post.title}
                                </h6>
                                <p
                                  style={{ fontSize: "12px" }}
                                  className="text-truncate-3"
                                >
                                  {`${post.summary.substring(0, 100)}...`}
                                </p>
                              </div>
                              <Link
                                to={`/blogs/${post.slug}`}
                                style={{ color: primaryColor }}
                                className="arrowLauncher-container text-decoration-none fw-semibold"
                              >
                                Read More{" "}
                                <LuArrowUpRight className="fs-5 arrowLauncher" />
                              </Link>
                            </Col>
                          </Row>
                        </CardBody>
                      </Card>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
              <div className="d-flex flex-row justify-content-center align-items-center mt-3 gap-3">
                <div className="custom-prev px-2 px-md-3">
                  <GrLinkPrevious />
                </div>
                <div className="custom-pagination d-flex gap-2 w-auto mt-1 mt-md-2" />
                <div className="custom-next px-2 px-md-3">
                  <GrLinkNext />
                </div>
              </div>
            </div>
          </Col>
        </Row>
      )}

      <Row className="mb-5">
        <Col xs={12}>
          <h1
            className="text-center"
            style={{ color: primaryColor, fontWeight: "bold" }}
          >
            Explore Insights by{" "}
            <span style={{ color: secondaryColor }}>Category</span>
          </h1>
          <p className="text-center text-muted mb-5">
            Discover curated insights, expert opinions, and trend forecasts
            across the domains that matter to your business growth.
          </p>

          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            id="blog-category-tabs"
            className="blogs-tabs mb-5"
          >
            <Tab eventKey="all" title="All Blogs">
              <Container fluid>
                <Row className="g-4 mt-2">
                  {blogPosts.map((post) => (
                    <Col key={post.id} xs={12} md={6} lg={4}>
                      <BlogCard post={post} />
                    </Col>
                  ))}
                  {blogPosts.length === 0 && (
                    <Col className="text-center text-muted py-5">
                      No blogs found in this category.
                    </Col>
                  )}
                </Row>
              </Container>
            </Tab>
            {categories.map((category) => (
              <Tab
                key={category.id}
                eventKey={category.id}
                title={category.name}
              >
                <Container fluid>
                  <Row className="g-4 mt-2">
                    {blogPosts.map((post) => (
                      <Col key={post.id} xs={12} md={6} lg={4}>
                        <BlogCard post={post} />
                      </Col>
                    ))}
                    {blogPosts.length === 0 && (
                      <Col className="text-center text-muted py-5">
                        No blogs found in this category.
                      </Col>
                    )}
                  </Row>
                </Container>
              </Tab>
            ))}
          </Tabs>
        </Col>
      </Row>
    </Container>
  );
};

export default Blogs;
